import {
  APP_ROUTES_ANC,
  APP_ROUTES_APP_ADMISSION,
  APP_ROUTES_APP_ASSIGNED,
  APP_ROUTES_APP_ENCOUNTERS,
  APP_ROUTES_BLOOD_BANK,
  APP_ROUTES_APP_INTAKE,
  APP_ROUTES_APP_PHARMACY_REPORTS,
  APP_ROUTES_APP_REPORTS,
  pharmacyWorkspaceHref,
  APP_ROUTES_APP_WAITLIST_DOCTORS,
  APP_ROUTES_APP_WAITLIST_NURSES,
  APP_ROUTES_APP_WAITLIST_RECEPTIONISTS,
  APP_ROUTES_APPOINTMENT,
  APP_ROUTES_ARCHIVES,
  APP_ROUTES_AUDIT,
  APP_ROUTES_BED_REQUEST,
  APP_ROUTES_BILLING,
  APP_ROUTES_CHARGE_ITEMS,
  APP_ROUTES_DEPARTMENT,
  APP_ROUTES_DRUG,
  APP_ROUTES_FACILITY,
  APP_ROUTES_FORMS,
  APP_ROUTES_HMO,
  APP_ROUTES_IMAGING,
  APP_ROUTES_INDEX,
  APP_ROUTES_LAB_TECH,
  APP_ROUTES_LABORATORY,
  APP_ROUTES_LABORATORY_DOC_TEMPLATE,
  APP_ROUTES_IMAGING_DOC_TEMPLATE,
  APP_ROUTES_PATIENT,
  APP_ROUTES_PATIENT_CHART,
  APP_ROUTES_PATIENT_DIRECTORY,
  APP_ROUTES_PERMISSION,
  APP_ROUTES_PRODUCT_MODULES,
  APP_ROUTES_PROCEDURE,
  APP_ROUTES_RADIO,
  APP_ROUTES_REGISTRATION_TEMPLATE,
  APP_ROUTES_SCHEDULE,
  APP_ROUTES_SHIFT,
  APP_ROUTES_STAFF,
  APP_ROUTES_UNITS
} from '@data'
import {
  AppointmentIcon,
  ArchiveIcon,
  AssignedIcon,
  AuditIcon,
  BedIcon,
  BillingIcon,
  ChargeItemIcon,
  DashboardIcon,
  DepartmentIcon,
  DispenseIcon,
  DrugBucketIcon,
  EncounterIcon,
  FacilityIcon,
  FormIcon,
  HMOIcon,
  HomeIcon,
  ImagingIcon,
  IntakeIcon,
  LaboratoryIcon,
  LocationIcon,
  MedicationIcon,
  MessageIcon,
  PatientDirectoryIcon,
  PatientsIcon,
  PermissionIcon,
  PharmacyIcon,
  ProcedureIcon,
  RadiologyIcon,
  ReportsIcon,
  ScheduleIcon,
  StaffIcon,
  UnitsIcon,
  WaitlistIcon
} from '@icons'
import { useUserRole } from '@hook/useUserRole'
import { useAppSelector } from '@store/hooks'
import { hasAccessToSidebar } from '@util'
import { useSidebarStore } from '@zustore/store' // <-- import the sidebar store
import { Box, BoxProps, Text } from 'grommet'
import React, { useState } from 'react'
import { GoChevronDown } from 'react-icons/go'
import { isPermissionAllowedByModules } from '../../../modules/access'
import { useProductModules } from '../../../modules/useProductModules'
import { SmallText } from '../Text'
import { SidebarButton, SidebarFooter } from './SidebarFooter'
import { SidebarHeader, UserSidebarHeader } from './SidebarHeader'

import classNames from 'classnames'
import { DocumentUser } from 'grommet-icons'
import { useLocation as ReactLocation } from 'react-router-dom'
import './style.css'

interface ISidebar extends BoxProps {
  children: React.ReactNode
  header: React.ReactNode
  footer?: React.ReactNode
}

type SidebarButtonProps = React.ComponentProps<typeof SidebarButton>

interface SidebarCollapsibleSectionProps extends BoxProps {
  icon: React.ReactNode
  label: string
  items: SidebarButtonProps[]
  contentMargin?: BoxProps['margin']
  className?: string
}

const SidebarCollapsibleSection: React.FC<SidebarCollapsibleSectionProps> = ({
  icon,
  label,
  items,
  contentMargin = { left: '2px' },
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { collapsed } = useSidebarStore()

  return (
    <Box {...rest} flex={false}>
      <Box
        align="center"
        className={`min-h-[auto] !w-fit ${collapsed ? 'mx-auto' : ''}`}
        direction="row"
        gap="small"
        onClick={() => setIsOpen(prev => !prev)}
        pad={{ horizontal: '6px', vertical: '12px' }}
        style={{ cursor: 'pointer' }}
      >
        {icon}
        {!collapsed && (
          <Box direction="row" gap="xxsmall">
            <SmallText>{label}</SmallText>
          </Box>
        )}
        <GoChevronDown
          className="ml-auto"
          style={{
            color: 'white',
            fontSize: '16px',
            marginTop: '4px',
            transform: isOpen ? 'rotate(180deg)' : undefined,
            transition: 'transform 0.2s ease'
          }}
        />
      </Box>
      {isOpen && (
        <Box gap={'4px'} margin={contentMargin}>
          {items?.map((item, index) => (
            <SidebarButton
              isDropDown
              key={`${label}-item-${index}`}
              {...item}
              className={classNames('!py-2 !ml-2', rest.className)}
            >
              {collapsed && item?.label}
            </SidebarButton>
          ))}
        </Box>
      )}
    </Box>
  )
}

/** Single nav item: permission-based visibility for all roles. One entry per path. */
type NavItem = {
  className?: string
  icon: React.ReactNode
  label: React.ReactNode
  permission?: string | string[]
  to: string
  disabled?: boolean
}

const MESSAGES_LABEL = (
  <Box direction="row" gap="xxsmall" justify="center">
    <span>Messages</span>
    <Box
      align="center"
      background="status-critical"
      height={{ min: '20px' }}
      justify="center"
      round="20px"
      width={{ min: '20px' }}
    >
      <Text size="10px">3</Text>
    </Box>
  </Box>
)

const MAIN_NAV_ITEMS: NavItem[] = [
  { icon: <DashboardIcon />, label: 'Dashboard', to: APP_ROUTES_INDEX },
  {
    icon: <PatientDirectoryIcon />,
    label: 'Patients Directory',
    permission: 'patient',
    to: APP_ROUTES_PATIENT_DIRECTORY
  },
  { icon: <PatientsIcon />, label: 'Patients', permission: 'patient', to: APP_ROUTES_PATIENT },
  {
    icon: <WaitlistIcon />,
    label: 'Doctors waiting List',
    permission: 'doctorswaitinglist',
    to: APP_ROUTES_APP_WAITLIST_DOCTORS
  },
  {
    icon: <WaitlistIcon />,
    label: 'Nurse waiting List',
    permission: 'nurseswaitinglist',
    to: APP_ROUTES_APP_WAITLIST_NURSES
  },
  {
    icon: <WaitlistIcon />,
    label: 'Receptionist waiting List',
    permission: 'receptionistswaitinglist',
    to: APP_ROUTES_APP_WAITLIST_RECEPTIONISTS
  },
  {
    icon: <IntakeIcon />,
    label: 'Intake',
    permission: ['receptionistsintake', 'nursesintake'],
    to: APP_ROUTES_APP_INTAKE
  },
  { icon: <MedicationIcon />, label: 'MAR', permission: 'mar', to: '/mar' },
  { icon: <DepartmentIcon />, label: 'Department', permission: 'department', to: APP_ROUTES_DEPARTMENT },
  { icon: <FacilityIcon />, label: 'Facility', permission: 'facility', to: APP_ROUTES_FACILITY },
  { icon: <ArchiveIcon />, label: 'Archives', permission: 'archives', to: APP_ROUTES_ARCHIVES },
  { icon: <StaffIcon />, label: 'Staff', permission: 'staff', to: APP_ROUTES_STAFF },
  { icon: <PermissionIcon />, label: 'Roles & Permissions', permission: 'role', to: APP_ROUTES_PERMISSION },
  { icon: <FacilityIcon />, label: 'Product Modules', to: APP_ROUTES_PRODUCT_MODULES },
  { icon: <LocationIcon />, label: 'Shift', permission: 'shift', to: APP_ROUTES_SHIFT },
  { icon: <ScheduleIcon />, label: 'Schedule', permission: 'schedule', to: APP_ROUTES_SCHEDULE },
  { icon: <AppointmentIcon />, label: 'Appointments', permission: 'appointment', to: APP_ROUTES_APPOINTMENT },
  { icon: <MedicationIcon />, label: 'Drug Formulary', permission: 'drug', to: APP_ROUTES_DRUG },
  {
    icon: <PharmacyIcon />,
    label: 'Med Order',
    permission: 'med order',
    to: pharmacyWorkspaceHref('Orders')
  },
  { icon: <ProcedureIcon />, label: 'Procedures', permission: 'procedure', to: APP_ROUTES_PROCEDURE },
  { icon: <LaboratoryIcon />, label: 'Lab Test', permission: 'lab_test', to: APP_ROUTES_LABORATORY },
  { icon: <LaboratoryIcon />, label: 'Laboratory', permission: 'laboratory', to: APP_ROUTES_LAB_TECH },
  { icon: <ImagingIcon />, label: 'Imaging', permission: 'imaging', to: APP_ROUTES_IMAGING },
  { icon: <RadiologyIcon />, label: 'Radiology', permission: 'radiology', to: APP_ROUTES_RADIO },
  { icon: <HMOIcon />, label: 'HMOs', permission: 'hmos', to: APP_ROUTES_HMO },
  { icon: <FormIcon />, label: 'Forms', permission: 'document template', to: APP_ROUTES_FORMS },
  {
    icon: <FormIcon />,
    label: 'Registration Temp.',
    permission: 'registration template',
    to: APP_ROUTES_REGISTRATION_TEMPLATE
  },
  {
    icon: <FormIcon />,
    label: 'Lab Document Temp.',
    permission: 'laboratory document template',
    to: APP_ROUTES_LABORATORY_DOC_TEMPLATE
  },
  {
    icon: <FormIcon />,
    label: 'Imaging Document Temp.',
    permission: 'imaging document template',
    to: APP_ROUTES_IMAGING_DOC_TEMPLATE
  },
  { icon: <BedIcon />, label: 'Bed Request', permission: 'bed request', to: APP_ROUTES_BED_REQUEST },
  { icon: <UnitsIcon />, label: 'Units', permission: 'units', to: APP_ROUTES_UNITS },
  { icon: <ChargeItemIcon />, label: 'Charge Items', permission: 'charge item', to: APP_ROUTES_CHARGE_ITEMS },
  { icon: <BillingIcon />, label: 'Billings', permission: 'billing', to: APP_ROUTES_BILLING },
  {
    icon: <ReportsIcon />,
    label: 'Reports',
    permission: 'reports',
    to: APP_ROUTES_APP_REPORTS
  },
  {
    icon: <ReportsIcon />,
    label: 'Pharmacy Reports',
    permission: 'pharmacy reports',
    to: APP_ROUTES_APP_PHARMACY_REPORTS
  },
  { icon: <EncounterIcon />, label: 'Encounters', permission: 'encounter', to: APP_ROUTES_APP_ENCOUNTERS },
  { icon: <LaboratoryIcon />, label: 'Blood bank', permission: 'encounter', to: APP_ROUTES_BLOOD_BANK },
  { icon: <PatientsIcon />, label: 'OB care', permission: 'antenatal', to: APP_ROUTES_ANC },
  {
    icon: <DrugBucketIcon />,
    label: 'Ward Requisitions',
    permission: 'nurse_requisition',
    to: pharmacyWorkspaceHref('Ward Requisitions')
  },
  {
    icon: <AssignedIcon />,
    label: 'Assigned Patients',
    permission: 'assigned patient',
    to: APP_ROUTES_APP_ASSIGNED
  },
  {
    icon: <BedIcon />,
    label: 'Pending Admits',
    permission: 'pending admits',
    to: APP_ROUTES_APP_ADMISSION
  },
  {
    className: '!hidden',
    icon: <MessageIcon />,
    label: MESSAGES_LABEL,
    permission: 'message',
    to: '/message'
  },
  { icon: <AuditIcon />, label: 'Audit Log', to: APP_ROUTES_AUDIT, permission: 'audit log' }
]

const MainNavigation = () => {
  const { collapsed } = useSidebarStore()
  const sidebarItems = useAppSelector(state => state.user.user?.sidebar_items)
  const { isSuperAdmin, isPharmacist } = useUserRole()
  const { enabledModules, bypassModuleGates, productSku } = useProductModules()
  const isPharmacyOnly = productSku === 'pharmacy_only'
  const pharmacyHrefMode = isPharmacyOnly ? 'community' : undefined

  /** Pharmacist focus: Dashboard + (hospital) Med Order; inventory/dispense sections below. */
  const pharmacyFocusOnly = isPharmacist && !isSuperAdmin

  const navItems = (
    pharmacyFocusOnly
      ? MAIN_NAV_ITEMS.filter(
          item =>
            item.to === APP_ROUTES_INDEX ||
            item.to === APP_ROUTES_APP_PHARMACY_REPORTS ||
            (!isPharmacyOnly && item.label === 'Med Order')
        )
      : MAIN_NAV_ITEMS
  ).filter(item => {
    if (item.to === APP_ROUTES_PRODUCT_MODULES) return isSuperAdmin
    if (isPharmacyOnly && (item.label === 'Med Order' || item.label === 'Ward Requisitions')) return false
    // pharmacy_only: clinic Reports is irrelevant — keep Pharmacy Reports only
    if (isPharmacyOnly && item.to === APP_ROUTES_APP_REPORTS) return false
    return (
      !item.permission || isPermissionAllowedByModules(item.permission, enabledModules, bypassModuleGates, productSku)
    )
  })

  /** Section-level gate. Mirrors SidebarButton's super-admin bypass so super admins,
   * who carry no sidebar_items, still see collapsible sections. */
  const canSeeSection = (permission: string | string[]) =>
    (isSuperAdmin || hasAccessToSidebar(sidebarItems || [], permission)) &&
    isPermissionAllowedByModules(permission, enabledModules, bypassModuleGates, productSku)

  const resolvedNavItems = navItems.map(item => {
    if (!isPharmacyOnly) return item
    if (item.to !== APP_ROUTES_APP_PHARMACY_REPORTS) return item
    return { ...item, label: 'Reports' }
  })

  return (
    <nav
      aria-label="main navigation"
      className={`section flex flex-col gap-1 overflow-auto ${collapsed ? 'sidebar-collapsed pl-0' : 'pl-4'}`}
    >
      {resolvedNavItems.map((item, index) => (
        <SidebarButton
          className={item.className}
          disabled={item.disabled}
          icon={item.icon}
          key={`nav-${item.to}-${index}`}
          label={item.label}
          permission={item.permission}
          to={item.to}
        />
      ))}
      {canSeeSection(['drug inventory', 'pharmacist_requisition']) && (
        <SidebarCollapsibleSection
          icon={<DrugBucketIcon />}
          items={[
            {
              className: 'text-sm',
              label: 'Product List',
              permission: 'drug inventory',
              to: pharmacyWorkspaceHref('Catalog', pharmacyHrefMode)
            },
            {
              className: 'text-sm',
              label: 'Issue',
              permission: 'drug inventory',
              to: pharmacyWorkspaceHref('Issue', pharmacyHrefMode)
            },
            {
              className: 'text-sm',
              label: 'Pharmacy Requisitions',
              permission: 'pharmacist_requisition',
              to: pharmacyWorkspaceHref('Requisitions', pharmacyHrefMode)
            }
          ]}
          label="Drug Inventory"
        />
      )}
      {canSeeSection('dispensings') && (
        <SidebarButton
          icon={<DispenseIcon />}
          label="Dispensing"
          permission="dispensings"
          to={pharmacyWorkspaceHref('Dispense', pharmacyHrefMode)}
        />
      )}
    </nav>
  )
}

const MainSidebar: React.FC<ISidebar> = ({ children, header, footer, ...rest }) => {
  const { collapsed } = useSidebarStore()
  return (
    <Box
      gap="medium"
      // height={{ min: '100%' }}
      pad="small"
      {...rest}
      className={collapsed ? 'sidebar-collapsed' : ''}
      overflow={'auto'}
      style={{
        maxWidth: collapsed ? '110px' : '225px',
        minWidth: collapsed ? '110px' : 'min(225px, 100%)',
        transition: 'max-width 0.3s, min-width 0.3s',
        width: '100%',
        flex: 1
      }}
    >
      {header}
      <Box
        flex
        overflow={'auto'}
        style={{
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}
      >
        {children}
      </Box>
      {footer}
    </Box>
  )
}

export const Sidebar = () => (
  <MainSidebar
    background="greenKelp"
    header={<SidebarHeader />}
    pad={{ top: 'medium' }}
    responsive={false}
    round="medium"
    width={{ min: '225px' }}
  >
    <MainNavigation />
  </MainSidebar>
)

export const UserSidebar = () => {
  const { pathname } = ReactLocation()
  const sidebarItems = useAppSelector(state => state.user.user?.sidebar_items)
  const { collapsed } = useSidebarStore()
  const { isPharmacist, isSuperAdmin } = useUserRole()
  const { enabledModules, bypassModuleGates, productSku } = useProductModules()
  const isPharmacyOnly = productSku === 'pharmacy_only'
  const pharmacyHrefMode = isPharmacyOnly ? 'community' : undefined
  const shouldShowPatientChart = pathname === APP_ROUTES_PATIENT_CHART || pathname.startsWith('/patient-chart')
  const pharmacyFocusOnly = isPharmacist && !isSuperAdmin

  const navItems = (
    pharmacyFocusOnly
      ? MAIN_NAV_ITEMS.filter(
          item => item.to === APP_ROUTES_APP_PHARMACY_REPORTS || (!isPharmacyOnly && item.label === 'Med Order')
        )
      : MAIN_NAV_ITEMS
  ).filter(item => {
    if (item.to === APP_ROUTES_PRODUCT_MODULES) return isSuperAdmin
    if (isPharmacyOnly && (item.label === 'Med Order' || item.label === 'Ward Requisitions')) return false
    if (isPharmacyOnly && item.to === APP_ROUTES_APP_REPORTS) return false
    return (
      !item.permission || isPermissionAllowedByModules(item.permission, enabledModules, bypassModuleGates, productSku)
    )
  })

  const resolvedNavItems = navItems.map(item => {
    if (!isPharmacyOnly) return item
    if (item.to !== APP_ROUTES_APP_PHARMACY_REPORTS) return item
    return { ...item, label: 'Reports' }
  })

  return (
    <MainSidebar
      background="greenKelp"
      footer={<SidebarFooter />}
      header={<UserSidebarHeader />}
      pad={{ top: 'medium' }}
      // responsive
      round="medium"
      width={{ min: '225px' }}
    >
      <nav className={`section flex flex-1 flex-col gap-1 overflow-auto ${collapsed ? 'sidebar-collapsed' : 'pl-4'}`}>
        <SidebarButton icon={<HomeIcon />} label="Home" to={APP_ROUTES_INDEX} />
        {shouldShowPatientChart && (
          <Box className="relative">
            <div className="absolute inset-0 cursor-not-allowed z-10" />
            <SidebarButton
              icon={<DocumentUser color="#81A16F" size="15px" />}
              label={"Patient's Chart"}
              to={APP_ROUTES_PATIENT_CHART}
            />
          </Box>
        )}
        {resolvedNavItems.map((item, index) => (
          <SidebarButton
            className={item.className}
            disabled={item.disabled}
            icon={item.icon}
            key={`user-nav-${item.to}-${index}`}
            label={item.label}
            permission={item.permission}
            to={item.to}
          />
        ))}
        {hasAccessToSidebar(sidebarItems || [], 'drug inventory') && (
          <SidebarCollapsibleSection
            icon={<DrugBucketIcon />}
            items={[
              {
                className: 'text-sm',
                label: 'Product List',
                to: pharmacyWorkspaceHref('Catalog', pharmacyHrefMode)
              }
            ]}
            label="Drug Inventory"
          />
        )}
        {hasAccessToSidebar(sidebarItems || [], 'dispensings') && (
          <SidebarButton
            icon={<DispenseIcon />}
            label="Dispensing"
            to={pharmacyWorkspaceHref('Dispense', pharmacyHrefMode)}
          />
        )}
      </nav>
    </MainSidebar>
  )
}
